/**
 * useTopupCoins hook
 * React Query hook for fetching the parent's non-expired top-up coin batches
 * (GET /parent/topup-coins, API-003, TS-007), soonest-expiry-first, plus totalRemaining.
 *
 * Implements TS-013 (T3): useTopupCoins hook.
 * Modelled directly on useParentSessions (frontend/src/hooks/useParentSessions.ts) —
 * same React Query shape, staleTime, error passthrough.
 *
 * Cache key: ['parent', 'topupCoins'] — separate from ['parent','dashboard'] and
 * ['parent','sessions'] so it never re-fires either of those on mount.
 * staleTime: 30s — consistent with useParentDashboard / useParentSessions convention.
 *
 * TO-LL-036: errors are never swallowed — React Query error object surfaces to callers.
 * TO-LL-044: returns TopupBatch[] with exact camelCase keys from the API contract.
 *
 * Decision D-4 (TS-013 implementation guide): batches are returned exactly as received —
 * NO client-side re-sort or re-filter. The backend guarantees non-expired + soonest-first
 * (CCM-05); re-deriving on the client would risk contract drift (TO-LL-003).
 */

import { useQuery } from '@tanstack/react-query';
import { getTopupCoins } from '@/services/parent';
import type { TopupBatch, TopupCoinsResponse } from '@/types/coins';

/**
 * Return type for useTopupCoins hook.
 * Modelled on UseParentSessionsReturn for consistency.
 */
export interface UseTopupCoinsReturn {
  /**
   * Non-expired batches, soonest-expiry-first (backend-ordered — never re-sorted here).
   * Empty array during loading or when the parent has no top-up coins.
   * TO-LL-044: each entry has exact camelCase keys (batchId, remaining, grantedAt,
   *   expiresAt, source).
   */
  batches: TopupBatch[];
  /** Sum of remaining across non-expired batches (0 during loading). */
  totalRemaining: number;
  /** True during initial fetch */
  isLoading: boolean;
  /** Error object if fetch failed; null on success. Never swallowed (TO-LL-036). */
  error: Error | null;
  /** Call to manually refetch */
  refetch: () => void;
}

/**
 * Hook for fetching and managing the parent's top-up coin batch detail.
 * Uses React Query with 30s staleTime — consistent with useParentDashboard/useParentSessions.
 *
 * Implements TS-013 (T3).
 */
export function useTopupCoins(): UseTopupCoinsReturn {
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery<TopupCoinsResponse, Error>({
    queryKey: ['parent', 'topupCoins'],
    queryFn: () => getTopupCoins(),
    staleTime: 30000, // 30 seconds — consistent with useParentDashboard / useParentSessions
  });

  // Defaults to [] / 0 during loading so callers never handle undefined.
  const batches: TopupBatch[] = data?.batches ?? [];
  const totalRemaining: number = data?.totalRemaining ?? 0;

  return {
    batches,
    totalRemaining,
    isLoading,
    error,
    refetch: () => {
      refetch();
    },
  };
}
